import { create } from 'zustand';
import i18n from '@/i18n';

export type Theme = 'dark' | 'light';
export type Lang = 'ar' | 'en';

/** The 8 narrative chapters + hero (plan §4, §5). */
export const CHAPTERS = [
  'hero',
  'leadership',
  'new-era',
  'architecture',
  'solutions',
  'impact',
  'ecosystem',
  'contact',
] as const;
export type ChapterId = (typeof CHAPTERS)[number];

interface UiState {
  theme: Theme;
  lang: Lang;
  dir: 'rtl' | 'ltr';
  audioEnabled: boolean;
  reducedMotion: boolean;
  activeChapter: ChapterId;

  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
  setLang: (l: Lang) => void;
  toggleLang: () => void;
  setAudio: (on: boolean) => void;
  toggleAudio: () => void;
  setReducedMotion: (on: boolean) => void;
  setActiveChapter: (c: ChapterId) => void;
}

const LS = {
  theme: 'valion.theme',
  lang: 'valion.lang',
  audio: 'valion.audio',
} as const;

function readTheme(): Theme {
  if (typeof document !== 'undefined') {
    const t = document.documentElement.getAttribute('data-theme');
    if (t === 'dark' || t === 'light') return t;
  }
  return 'dark';
}

function readLang(): Lang {
  if (typeof document !== 'undefined') {
    const l = document.documentElement.getAttribute('lang');
    if (l === 'ar' || l === 'en') return l;
  }
  return 'ar';
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );
}

/** Apply lang + dir to <html> and keep i18next in sync (plan §7). */
function applyLang(lang: Lang) {
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  if (typeof document !== 'undefined') {
    const d = document.documentElement;
    d.setAttribute('lang', lang);
    d.setAttribute('dir', dir);
  }
  if (i18n.language !== lang) void i18n.changeLanguage(lang);
  return dir;
}

function applyTheme(theme: Theme) {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
    const meta = document.querySelector('meta[name="theme-color"]');
    meta?.setAttribute('content', theme === 'dark' ? '#06080C' : '#F1F0F1');
  }
}

const initialLang = readLang();

export const useUiStore = create<UiState>((set, get) => ({
  theme: readTheme(),
  lang: initialLang,
  dir: initialLang === 'ar' ? 'rtl' : 'ltr',
  audioEnabled: false, // corporate-safe default: off (plan §3.1)
  reducedMotion: prefersReducedMotion(),
  activeChapter: 'hero',

  setTheme: (theme) => {
    applyTheme(theme);
    try {
      localStorage.setItem(LS.theme, theme);
    } catch {
      /* storage blocked */
    }
    set({ theme });
  },
  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),

  setLang: (lang) => {
    const dir = applyLang(lang);
    try {
      localStorage.setItem(LS.lang, lang);
    } catch {
      /* storage blocked */
    }
    set({ lang, dir });
  },
  toggleLang: () => get().setLang(get().lang === 'ar' ? 'en' : 'ar'),

  setAudio: (audioEnabled) => {
    try {
      localStorage.setItem(LS.audio, String(audioEnabled));
    } catch {
      /* storage blocked */
    }
    set({ audioEnabled });
  },
  toggleAudio: () => get().setAudio(!get().audioEnabled),

  setReducedMotion: (reducedMotion) => set({ reducedMotion }),
  setActiveChapter: (activeChapter) => set({ activeChapter }),
}));
